// src/services/prescriptionService.js

import apiClient from "./apiClient";
import { enrichPrescriptionMedicinesWithNames } from "../utils/prescriptionMedicineUtils";
import { linkPrescriptionToAppointment } from "./appointmentService";

const API_BASE_URL = `/prescriptions`; // Use relative path since apiClient has baseURL

const toPositiveRelationId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    if (Array.isArray(value)) return null;
    return toPositiveRelationId(
      value.id ??
        value.appointmentId ??
        value.appointment_id ??
        value.prescriptionId ??
        value.prescription_id
    );
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
};

const resolvePrescriptionRecordId = (record) => {
  if (record == null || record === "") return null;
  if (typeof record !== "object") return toPositiveRelationId(record);
  return toPositiveRelationId(
    record.id ??
      record.prescriptionId ??
      record.prescription_id ??
      record.data?.id ??
      record.data?.prescriptionId
  );
};

/**
 * Send appointmentId as both camelCase and snake_case so org prescription
 * inserts persist appointment_id regardless of Jackson naming.
 * Omit the field when missing so IPD / self-book lab payloads stay unchanged.
 */
export const normalizePrescriptionRelationIds = (prescription = {}) => {
  if (!prescription || typeof prescription !== "object" || Array.isArray(prescription)) {
    return prescription;
  }

  const payload = { ...prescription };
  const appointmentId = toPositiveRelationId(
    payload.appointmentId ?? payload.appointment_id ?? payload.appointment
  );

  if (appointmentId != null) {
    payload.appointmentId = appointmentId;
    payload.appointment_id = appointmentId;
  } else {
    delete payload.appointmentId;
    delete payload.appointment_id;
  }

  return payload;
};

const linkPrescriptionAppointmentIfPossible = async (
  requestPayload,
  createdPrescription,
  appointmentSnapshot
) => {
  const appointmentId = toPositiveRelationId(
    requestPayload?.appointmentId ?? requestPayload?.appointment_id
  );
  const prescriptionId = resolvePrescriptionRecordId(createdPrescription);
  if (!appointmentId || !prescriptionId) return;

  try {
    await linkPrescriptionToAppointment(
      appointmentId,
      prescriptionId,
      appointmentSnapshot
    );
  } catch (linkError) {
    // Prescription already saved — do not fail the doctor submit for the reverse link.
    console.warn(
      "[prescriptionService] Prescription created but appointment.prescription_id was not updated:",
      linkError
    );
  }
};

const finalizePatientPrescriptions = async (prescriptions) =>
  enrichPrescriptionMedicinesWithNames(Array.isArray(prescriptions) ? prescriptions : []);

  
export const getPrescriptionById = async (id) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_BASE_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching prescription:", error);
    throw error;
  }
};

export const createPrescription = async (prescription, options = {}) => {
  try {
    // Get organizationId for logging
    const storedUser = localStorage.getItem("user");
    let organizationId = null;
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        organizationId = user?.organizationId || localStorage.getItem("organizationId");
        if (organizationId && typeof organizationId === 'string') {
          organizationId = parseInt(organizationId, 10);
        }
      } catch (e) {
        // ignore
      }
    }
    
    // Remove organizationId from prescription object if it exists (apiClient will add it as query param)
    const { organizationId: orgIdInBody, ...prescriptionWithoutOrg } = prescription;
    const prescriptionData = normalizePrescriptionRelationIds(prescriptionWithoutOrg);
    
    console.log("[prescriptionService] ========== CREATING PRESCRIPTION ==========");
    console.log("[prescriptionService] Organization ID:", organizationId);
    console.log("[prescriptionService] Patient ID:", prescriptionData.patientId);
    console.log("[prescriptionService] Appointment ID:", prescriptionData.appointmentId);
    console.log("[prescriptionService] Complaints:", prescriptionData.complaints);
    console.log("[prescriptionService] Diagnoses:", prescriptionData.diagnoses);
    console.log("[prescriptionService] Vital Signs:", prescriptionData.vitalSigns);
    console.log("[prescriptionService] Medicines count:", prescriptionData.medicines?.length || 0);
    console.log("[prescriptionService] Lab Tests:", prescriptionData.patientLabTests);
    console.log("[prescriptionService] Full prescription payload:", JSON.stringify(prescriptionData, null, 2));
    console.log("[prescriptionService] apiClient will automatically add organizationId as query parameter");
    
    // apiClient will automatically add organizationId as query parameter via interceptor
    // (not in body, as Prescription DTO needs it as query param to look up lab tests in org-specific table)
    const response = await apiClient.post(API_BASE_URL, prescriptionData);
    
    console.log("[prescriptionService] Prescription created successfully!");
    console.log("[prescriptionService] Response:", response.data);
    console.log("[prescriptionService] ===========================================");

    await linkPrescriptionAppointmentIfPossible(
      prescriptionData,
      response.data,
      options?.appointment || null
    );
    
    return response.data;
  } catch (error) {
    console.error("[prescriptionService] ========== ERROR CREATING PRESCRIPTION ==========");
    console.error("[prescriptionService] Error:", error);
    console.error("[prescriptionService] Error response:", error.response?.data);
    console.error("[prescriptionService] Error status:", error.response?.status);
    console.error("[prescriptionService] Error message:", error.response?.data?.message);
    console.error("[prescriptionService] =================================================");
    
    // Provide more helpful error message
    if (error.response?.status === 500) {
      const backendMessage = error.response?.data?.message || '';
      if (backendMessage.includes('Patient not found')) {
        const patientId = prescription.patientId;
        throw new Error(`Patient not found: The patient with ID ${patientId} does not exist in the organization-specific patient table. Please ensure the patient exists in the correct organization table.`);
      }
      // Check for complaints/diagnoses/vital signs related errors
      if (backendMessage.includes('complaint') || backendMessage.includes('Complaint')) {
        console.error("[prescriptionService] ⚠️ Error related to complaints - backend may not be saving complaints correctly");
      }
      if (backendMessage.includes('diagnosis') || backendMessage.includes('Diagnosis')) {
        console.error("[prescriptionService] ⚠️ Error related to diagnoses - backend may not be saving diagnoses correctly");
      }
      if (backendMessage.includes('vital') || backendMessage.includes('Vital')) {
        console.error("[prescriptionService] ⚠️ Error related to vital signs - backend may not be saving vital signs correctly");
      }
    }
    
    throw error;
  }
};

export const updatePrescription = async (id, prescription) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(
      `${API_BASE_URL}/${id}`,
      normalizePrescriptionRelationIds(prescription)
    );
    return response.data;
  } catch (error) {
    console.error("Error updating prescription:", error);
    throw error;
  }
};

export const getPrescriptionsByDoctorId = async (doctorId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_BASE_URL}/doctor/${doctorId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching prescriptions by doctor:", error);
    throw error;
  }
};

export const getPrescriptionsByPatientId = async (patientId) => {
  try {
    const storedUser = localStorage.getItem("user");
    let organizationId = null;
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        organizationId = user?.organizationId || localStorage.getItem("organizationId");
        // Convert to number if it's a string
        if (organizationId && typeof organizationId === 'string') {
          organizationId = parseInt(organizationId, 10);
        }
      } catch (e) {
        console.error("[prescriptionService] Error parsing stored user:", e);
      }
    }
    
    console.log(`[prescriptionService] getPrescriptionsByPatientId: patientId=${patientId}, organizationId=${organizationId}`);
    
    // Explicitly add organizationId as query parameter to ensure it's sent
    // apiClient interceptor will also add it, but being explicit ensures it's there
    const config = {
      params: {}
    };
    
    if (organizationId && organizationId > 0) {
      config.params.organizationId = organizationId;
      console.log(`[prescriptionService] Adding organizationId=${organizationId} to request params`);
    } else {
      console.warn(`[prescriptionService] No valid organizationId found (value: ${organizationId})`);
    }
    
    // apiClient will automatically add organizationId as query param via interceptor
    // We're also explicitly adding it above to ensure it's sent
    const response = await apiClient.get(`${API_BASE_URL}/patient/${patientId}`, config);
    
    console.log(`[prescriptionService] Response status: ${response.status}`);
    console.log(`[prescriptionService] Response data type: ${typeof response.data}`);
    console.log(`[prescriptionService] Response data length: ${Array.isArray(response.data) ? response.data.length : 'not an array'}`);
    
    // Ensure response.data is an array. Medicines (with names), complaints,
    // and diagnoses are expected embedded directly on each prescription now —
    // the previous per-prescription "medicines came back empty, try up to 3
    // fallback endpoints" workaround is gone; finalizePatientPrescriptions
    // still resolves medicine names from the catalog as a zero-cost safety
    // net (it only makes a network call if a medicine is genuinely missing
    // a name), but no longer needs to re-fetch medicines wholesale.
    if (Array.isArray(response.data)) {
      return finalizePatientPrescriptions(response.data);
    } else if (response.data && typeof response.data === 'object') {
      // If backend returns an object, try to extract array from it
      if (Array.isArray(response.data.prescriptions)) {
        return finalizePatientPrescriptions(response.data.prescriptions);
      } else if (Array.isArray(response.data.data)) {
        return finalizePatientPrescriptions(response.data.data);
      }
    }
    
    return [];
  } catch (error) {
    // Handle case where organization-specific table doesn't exist yet or backend error
    if (error.response?.status === 500 && 
        (error.response?.data?.message?.includes("Failed to retrieve") ||
         error.response?.data?.message?.includes("from table") ||
         error.response?.data?.message?.includes("does not exist"))) {
      return []; // Return empty array if table doesn't exist
    }
    
    // Return empty array instead of throwing to prevent UI crashes
    return [];
  }
};

export const getPrescriptionsWithLabTest = async () => {
  try {
    // Get organizationId to ensure it's available and log it
    const storedUser = localStorage.getItem("user");
    let organizationId = null;
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        organizationId = parsedUser?.organizationId || localStorage.getItem("organizationId");
        if (organizationId && typeof organizationId === 'string') {
          organizationId = parseInt(organizationId, 10);
        }
      } catch (e) {
        console.error("[prescriptionService] Error parsing stored user:", e);
      }
    }
    
    console.log(`[prescriptionService] getPrescriptionsWithLabTest - organizationId: ${organizationId}`);
    
    // apiClient will automatically add organizationId as query param via interceptor
    // But we'll also explicitly add it to ensure it's sent
    const config = organizationId && organizationId > 0 
      ? { params: { organizationId } }
      : {};
    
    const response = await apiClient.get(`${API_BASE_URL}/withlabtest`, config);
    
    console.log(`[prescriptionService] getPrescriptionsWithLabTest response: ${Array.isArray(response.data) ? response.data.length : 0} prescriptions`);
    if (Array.isArray(response.data) && response.data.length > 0) {
      console.log(`[prescriptionService] Sample prescription keys:`, Object.keys(response.data[0]));
      console.log(`[prescriptionService] Sample prescription organizationId:`, response.data[0]?.organizationId);
    }
    
    return response.data;
  } catch (error) {
    console.error("Error fetching prescriptions with lab test:", error);
    throw error;
  }
};

export const getOpdLabTests = async () => {
  try {
    // Get organizationId to ensure it's available and log it
    const storedUser = localStorage.getItem("user");
    let organizationId = null;
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        organizationId = parsedUser?.organizationId || localStorage.getItem("organizationId");
        if (organizationId && typeof organizationId === 'string') {
          organizationId = parseInt(organizationId, 10);
        }
      } catch (e) {
        console.error("[prescriptionService] Error parsing stored user:", e);
      }
    }
    
    console.log(`[prescriptionService] getOpdLabTests - organizationId: ${organizationId}`);
    
    // apiClient will automatically add organizationId as query param via interceptor
    // But we'll also explicitly add it to ensure it's sent
    const config = organizationId && organizationId > 0 
      ? { params: { organizationId } }
      : {};
    
    const response = await apiClient.get(`${API_BASE_URL}/opdlabtests`, config);
    
    console.log(`[prescriptionService] getOpdLabTests response: ${Array.isArray(response.data) ? response.data.length : 0} prescriptions`);
    if (Array.isArray(response.data) && response.data.length > 0) {
      console.log(`[prescriptionService] Sample prescription keys:`, Object.keys(response.data[0]));
      console.log(`[prescriptionService] Sample prescription organizationId:`, response.data[0]?.organizationId);
    }
    
    return response.data;
  } catch (error) {
    console.error("Error fetching opd lab tests:", error);
    throw error;
  }
};
